import React, { useState } from 'react';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { validateContactForm, validateField } from '../utils/validation';
import GlassCard from '../components/GlassCard';
import GlassInput from '../components/forms/GlassInput';
import GlassTextarea from '../components/forms/GlassTextarea';
import GlassButton from '../components/forms/GlassButton';

/**
 * Contact page with form and pharmacy information
 * Implements Requirements 16.1, 16.2, 16.3, 16.7, 16.8
 */
const Contact = () => {
  const { verifyRecaptcha } = useRecaptcha();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation
    if (errors[name]) {
      const validation = validateField(name, value, formData);
      setErrors(prev => ({
        ...prev,
        [name]: validation.isValid ? null : validation.error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const validation = validateField(name, value, formData);
    setErrors(prev => ({
      ...prev,
      [name]: validation.isValid ? null : validation.error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate entire form
    const validation = validateContactForm(formData);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Verify reCAPTCHA (optional — backend allows null when key not configured)
      const recaptchaToken = await verifyRecaptcha('contact_form');

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recaptchaToken })
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setErrors({});
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setErrors({ submit: 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4">
          <GlassCard className="p-8 text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-gray-800 mb-4">Message Sent Successfully!</h1>
            <p className="text-gray-600 mb-6">
              Thank you for contacting us. We'll get back to you within 24 hours.
            </p>
            <GlassButton onClick={() => setSubmitSuccess(false)}>
              Send Another Message
            </GlassButton>
          </GlassCard>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <GlassCard className="p-8">
            <h1 className="text-gray-800 dark:text-white mb-6">Get in Touch</h1>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <GlassInput
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.name}
                  required
                  autoComplete="name"
                />
                
                <GlassInput
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.email}
                  required
                  autoComplete="email"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <GlassInput
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.phone}
                  autoComplete="tel"
                />
                
                <GlassInput
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.subject}
                  required
                />
              </div>
              
              <GlassTextarea
                label="Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.message}
                required
                rows={6}
                maxLength={1000}
                placeholder="Please describe how we can help you..."
              />
              
              {errors.submit && (
                <div className="p-3 bg-red-100/50 border border-red-200 rounded-lg text-red-700">
                  {errors.submit}
                </div>
              )}
              
              <GlassButton
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Sending Message...' : 'Send Message'}
              </GlassButton>
            </form>
          </GlassCard>
          
          {/* Contact Information */}
          <div className="space-y-6">
            <GlassCard className="p-8">
              <h2 className="text-gray-800 dark:text-white mb-6">Contact Information</h2>
              
              <div className="space-y-4">
                <a 
                  href="https://maps.app.goo.gl/aNLgSwfv4Nzw9Aj5A" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-start space-x-4 hover:bg-white/10 dark:hover:bg-slate-700/30 p-2 rounded-lg transition-colors duration-200 group -ml-2"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-glass-blue to-glass-green rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 616 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-800 dark:text-white group-hover:text-glass-blue dark:group-hover:text-blue-400 transition-colors">Address</h3>
                    <p className="text-gray-600 dark:text-gray-300">OUTERING ROAD BEHIND EASTMART SUPERMARKET<br />RUARAKA, NAIROBI</p>
                  </div>
                </a>
                
                <a 
                  href="tel:0768103599"
                  className="flex items-start space-x-4 hover:bg-white/10 dark:hover:bg-slate-700/30 p-2 rounded-lg transition-colors duration-200 group -ml-2"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-glass-blue to-glass-green rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-800 dark:text-white group-hover:text-glass-blue dark:group-hover:text-blue-400 transition-colors">Phone</h3>
                    <p className="text-gray-600 dark:text-gray-300">0768103599</p>
                  </div>
                </a>
                
                <a 
                  href="mailto:esenapharmacy@gmail.com"
                  className="flex items-start space-x-4 hover:bg-white/10 dark:hover:bg-slate-700/30 p-2 rounded-lg transition-colors duration-200 group -ml-2"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-glass-blue to-glass-green rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-800 dark:text-white group-hover:text-glass-blue dark:group-hover:text-blue-400 transition-colors">Email</h3>
                    <p className="text-gray-600 dark:text-gray-300">esenapharmacy@gmail.com</p>
                  </div>
                </a>
              </div>
            </GlassCard>
            
            <GlassCard className="p-8">
              <h2 className="text-gray-800 dark:text-white mb-6">Business Hours</h2>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Monday - Friday</span>
                  <span className="text-gray-800 dark:text-white">8:00 AM - 8:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Saturday</span>
                  <span className="text-gray-800 dark:text-white">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Sunday</span>
                  <span className="text-gray-800 dark:text-white">10:00 AM - 4:00 PM</span>
                </div>
              </div>
            </GlassCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;